import React from "react";
import Skeleton from "react-loading-skeleton";

interface UnifiedTransactionDetailsSkeletonProps {
  width?: string;
}

const UnifiedTransactionDetailsSkeleton: React.FC<
  UnifiedTransactionDetailsSkeletonProps
> = ({ width = "w-full" }) => {
  return (
    <div
      className={`flex flex-col gap-[12px] mt-[16px] ${width}`}
      style={{ height: "65dvh" }}
    >
      {/* Transaction Header Section */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between">
          <div className="bg-bg-page-body py-[4px] px-[12px] text-text-base font-bold text-[10px] rounded">
            Txn ID: ....
          </div>
          <Skeleton height={20} width={60} />
          <Skeleton height={20} width={70} />
        </div>

        {/* Transaction Type */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Transaction Type
          </span>
          <Skeleton height={12} width={120} />
        </div>

        {/* User Email */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            User Email
          </span>
          <Skeleton height={12} width={180} />
        </div>

        {/* Full Name */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Full Name
          </span>
          <Skeleton height={12} width={140} />
        </div>

        {/* Merchant Name */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Merchant Name
          </span>
          <Skeleton height={12} width={160} />
        </div>

        {/* Transaction Date & Time */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Trx Date & Time
          </span>
          <Skeleton height={12} width={150} />
        </div>

        <div className="border-b border-border-subtle"></div>

        {/* MID | UID */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            MID | UID
          </span>
          <Skeleton height={12} width={200} />
        </div>
      </div>

      {/* Payment Details Section */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row items-center gap-[4px]">
          <div className="bg-bg-page-body rounded-full h-[24px] p-[2px] w-[24px] mr-[4px] flex items-center justify-center">
            <Skeleton circle height={16} width={16} />
          </div>
          <span className="text-[14px] font-normal text-text-base">
            Payment Details
          </span>
        </div>

        {/* Base Currency */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Base Currency
          </span>
          <div className="flex flex-row gap-[4px] items-center">
            <Skeleton circle height={14} width={14} />
            <Skeleton height={12} width={50} />
          </div>
        </div>

        {/* Settled Currency */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Settled Currency
          </span>
          <div className="flex flex-row gap-[4px] items-center">
            <Skeleton circle height={14} width={14} />
            <Skeleton height={12} width={50} />
          </div>
        </div>

        {/* Network */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Network
          </span>
          <Skeleton height={12} width={80} />
        </div>

        {/* OID */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">OID</span>
          <Skeleton height={12} width={120} />
        </div>

        {/* MOID */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">MOID</span>
          <Skeleton height={12} width={100} />
        </div>

        {/* Base Amount */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Base Amount
          </span>
          <Skeleton height={12} width={100} />
        </div>

        {/* Requested Amount */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Requested Amount
          </span>
          <Skeleton height={12} width={100} />
        </div>

        {/* Conversion Rate */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Conversion Rate
          </span>
          <Skeleton height={12} width={80} />
        </div>

        {/* Commission Amount */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Commission Amount
          </span>
          <Skeleton height={12} width={90} />
        </div>

        {/* Network Fees */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Network Fees
          </span>
          <Skeleton height={12} width={90} />
        </div>

        {/* Total Fees */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Total Fees
          </span>
          <Skeleton height={12} width={90} />
        </div>

        {/* Received Amount */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Received Amount
          </span>
          <Skeleton height={12} width={100} />
        </div>

        {/* Base Amount Received */}
        <div className="flex flex-row justify-between items-center">
          <span className="text-text-base text-[10px] font-medium">
            Base Amount Received
          </span>
          <Skeleton height={12} width={100} />
        </div>
      </div>

      {/* Transaction Details Section */}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="rounded-lg flex flex-col my-2">
          <div className="flex flex-col rounded-lg p-[8px] bg-bg-trx-card-header rounded-t-lg rounded-b-none">
            <span className="text-[12px] font-semibold text-center">
              Transaction Details
            </span>
          </div>
          <div className="flex flex-col bg-[#FFFFFF] p-[8px] rounded-t-none rounded-b-lg">
            {/* From Address */}
            <div className="flex flex-row justify-between mb-1">
              <span className="font-medium text-[12px] text-text-label">
                From address
              </span>
              <Skeleton height={16} width={16} />
            </div>
            <Skeleton height={12} width="100%" className="mb-2" />

            {/* To Address */}
            <div className="flex flex-row justify-between">
              <span className="font-medium text-[12px] text-text-label">
                To address
              </span>
              <Skeleton height={16} width={16} />
            </div>
            <Skeleton height={12} width="100%" className="mt-1" />

            {/* Transaction Hash */}
            <div className="flex flex-row justify-between w-full mt-[12px]">
              <span className="font-medium text-[12px] text-text-label">
                Transaction Hash
              </span>
              <Skeleton height={16} width={16} />
            </div>
            <Skeleton height={12} width="100%" className="mt-1" />

            {/* Amount, Confirmations, Status */}
            <div className="p-[8px] mt-[12px] flex flex-row justify-between w-full border-t border-t-border-subtle">
              <div className="flex flex-col items-center">
                <span className="font-medium text-[12px] text-text-label">
                  Amount
                </span>
                <Skeleton height={12} width={80} />
              </div>
              <div className="w-px bg-border-subtle" />
              <div className="flex flex-col items-center">
                <span className="font-medium text-[12px] text-text-label">
                  Confirmations
                </span>
                <Skeleton height={12} width={60} />
              </div>
              <div className="w-px bg-border-subtle" />
              <div className="flex flex-col">
                <span className="font-medium text-[12px] text-text-label text-right">
                  Status
                </span>
                <Skeleton height={12} width={70} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedTransactionDetailsSkeleton;
